import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Filter, X } from "lucide-react";
import { ViewSwitcher } from "@/components/common/ViewSwitcher";
import { SearchBar } from "@/components/common/SearchBar";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useCases } from "@/hooks/useCases";
import CaseForm from "../components/cases/CaseForm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_LABELS = {
  preparation: "Preparação",
  consultation: "Consulta",
  in_progress: "Em andamento",
  pending: "Pendente",
  approved: "Aprovado",
  denied: "Negado",
  closed: "Fechado",
  archived: "Arquivado",
};

const PRIORITY_LABELS = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

export default function Cases() {
  const navigate = useNavigate();
  const { cases, isLoading, error, refetch } = useCases();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [caseTypeFilter, setCaseTypeFilter] = useState("");

  const caseTypes = useMemo(() => {
    const types = new Set();
    cases.forEach((c) => {
      if (c.case_type_name) types.add(c.case_type_name);
    });
    return Array.from(types).sort();
  }, [cases]);

  const hasActiveFilters = statusFilter || priorityFilter || caseTypeFilter;

  const clearFilters = () => {
    setStatusFilter("");
    setPriorityFilter("");
    setCaseTypeFilter("");
  };

  const filteredCases = useMemo(() => {
    return cases.filter((caseItem) => {
      // Text search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          caseItem.case_type_name?.toLowerCase().includes(query) ||
          caseItem.status?.toLowerCase().includes(query) ||
          caseItem.id?.toLowerCase().includes(query) ||
          caseItem.created_by_display_name?.toLowerCase().includes(query) ||
          caseItem.deal_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      // Status filter
      if (statusFilter && caseItem.status !== statusFilter) return false;
      // Priority filter
      if (priorityFilter && caseItem.priority !== priorityFilter) return false;
      // Case type filter
      if (caseTypeFilter && caseItem.case_type_name !== caseTypeFilter) return false;
      return true;
    });
  }, [searchQuery, cases, statusFilter, priorityFilter, caseTypeFilter]);

  const handleViewCase = (caseId) => {
    navigate(createPageUrl("CaseDetail") + "?id=" + caseId);
  };

  const handleCaseCreated = (newCase) => {
    setShowCreateForm(false);
    refetch();
    navigate(createPageUrl("CaseDetail") + "?id=" + newCase.id);
  };

  const statusColors = {
    open: "bg-slate-100 text-slate-700 border-0",
    consultation: "bg-slate-100 text-slate-700 border-0",
    in_progress: "bg-blue-100 text-blue-700 border-0",
    pending: "bg-amber-100 text-amber-700 border-0",
    approved: "bg-emerald-100 text-emerald-700 border-0",
    denied: "bg-rose-100 text-rose-700 border-0",
    closed: "bg-slate-100 text-slate-500 border-0",
    archived: "bg-slate-100 text-slate-500 border-0",
  };

  const priorityColors = {
    low: "bg-slate-100 text-slate-600 border-0",
    medium: "bg-blue-100 text-blue-600 border-0",
    high: "bg-orange-100 text-orange-600 border-0",
    urgent: "bg-rose-100 text-rose-600 border-0",
  };

  const getCaseNumber = (caseId) => {
    if (!caseId) return "";
    return `#2025-${caseId.substring(0, 4).toUpperCase()}`;
  };

  const getContactInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex-shrink-0 p-4 md:p-6 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCreateForm(true)}
              className="rounded-full h-9 w-9 p-0 flex-shrink-0 bg-primary text-white hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters((v) => !v)}
              className={`rounded-full h-9 w-9 p-0 flex-shrink-0 relative hover:bg-slate-200 ${showFilters ? "bg-slate-200 dark:bg-slate-700" : ""}`}
            >
              <Filter className="w-4 h-4" />
              {hasActiveFilters && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-primary rounded-full" />
              )}
            </Button>

            <ViewSwitcher viewMode={viewMode} onViewChange={setViewMode} />

            <SearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar processos..."
              className="flex-1 max-w-sm"
            />
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="flex-shrink-0 px-4 md:px-6 pb-3">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 bg-white dark:bg-slate-800 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="h-9 bg-white dark:bg-slate-800 text-sm">
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Select value={caseTypeFilter} onValueChange={setCaseTypeFilter}>
                  <SelectTrigger className="h-9 bg-white dark:bg-slate-800 text-sm flex-1">
                    <SelectValue placeholder="Tipo de processo" />
                  </SelectTrigger>
                  <SelectContent>
                    {caseTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="rounded-full h-9 w-9 p-0 flex-shrink-0 hover:bg-slate-200"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 md:px-6 pb-4">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-slate-500 dark:text-slate-400">Carregando processos...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-rose-600">{error instanceof Error ? error.message : "Falha ao carregar processos"}</p>
              <Button onClick={() => refetch()} className="mt-4">
                Tentar novamente
              </Button>
            </div>
          ) : !filteredCases || filteredCases.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 dark:text-slate-400">Nenhum processo encontrado</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCases.map((caseItem) => (
                <Card
                  key={caseItem.id}
                  className="border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all cursor-pointer bg-white dark:bg-slate-800"
                  onClick={() => handleViewCase(caseItem.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                            {caseItem.case_type_name || caseItem.sales_item_name || caseItem.deal_name || "Desconhecido"}
                          </h3>
                          <Badge className={`${statusColors[caseItem.status] || statusColors.consultation} font-normal text-xs px-3 py-1 rounded-full`}>
                            {STATUS_LABELS[caseItem.status] || caseItem.status || "consulta"}
                          </Badge>
                          <Badge className={`${priorityColors[caseItem.priority] || priorityColors.medium} font-normal text-xs px-3 py-1 rounded-full`}>
                            {PRIORITY_LABELS[caseItem.priority] || caseItem.priority || "média"}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {getContactInitials(caseItem.created_by_display_name || "U")}
                            </span>
                          </div>
                          <span className="text-blue-600 font-medium hover:underline">
                            {caseItem.created_by_display_name || "Desconhecido"}
                          </span>
                          <span className="text-slate-400 dark:text-slate-500">•</span>
                          <span className="text-slate-600 dark:text-slate-400 font-mono text-sm">
                            {getCaseNumber(caseItem.id)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <CaseForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSuccess={handleCaseCreated}
      />
    </div>
  );
}
